// ViewReadService.js

/**
 * ViewReadService.js
 *
 * Service for reading view data from Firestore.
 * When `nodeType` is set, merges live system-type views
 * (`_nodetypes/{typeKey}/views`) ahead of base-scoped `_views`.
 */

// Firebase
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';
import {
  mergeSystemAndBaseViews,
  subscribeToTypeViews,
} from 'services/views/TypeViewService';

// Constants
const VIEWS_COLLECTION = collections.views;
const sharedViewSubscriptions = new Map();

const normalizeScopeValue = (value) => String(value || '').trim();

function buildViewScope({ userKey, baseKey, nodeType } = {}) {
  return {
    userKey: normalizeScopeValue(userKey),
    baseKey: normalizeScopeValue(baseKey),
    nodeType: normalizeScopeValue(nodeType),
  };
}

function buildViewScopeKey(scope) {
  return [
    `userKey=${scope.userKey || '*'}`,
    `baseKey=${scope.baseKey || '*'}`,
    `nodeType=${scope.nodeType || '*'}`,
  ].join('|');
}

function buildViewConstraints(scope) {
  const constraints = [];
  if (scope.userKey) constraints.push(where('userKey', '==', scope.userKey));
  if (scope.baseKey) constraints.push(where('baseKey', '==', scope.baseKey));
  if (scope.nodeType) constraints.push(where('nodeType', '==', scope.nodeType));
  return constraints;
}

function ensureSharedViewSubscription(scope) {
  const scopeKey = buildViewScopeKey(scope);
  const existing = sharedViewSubscriptions.get(scopeKey);
  if (existing) return existing;

  const entry = {
    scope,
    scopeKey,
    rows: [],
    listeners: new Set(),
    unsubscribe: null,
  };

  const constraints = buildViewConstraints(scope);
  const q = query(collection(db, VIEWS_COLLECTION), ...constraints);

  entry.unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      entry.rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
      }));

      entry.listeners.forEach((listener) => {
        try {
          listener(entry.rows);
        } catch (error) {
          console.error('[ViewReadService] shared listener error:', error);
        }
      });
    },
    (error) => {
      console.error('[ViewReadService] subscribeToViews error:', error);
      entry.rows = [];
      entry.listeners.forEach((listener) => listener([]));
    }
  );

  sharedViewSubscriptions.set(scopeKey, entry);
  return entry;
}

function attachSharedListener(scope, callback) {
  const entry = ensureSharedViewSubscription(scope);
  entry.listeners.add(callback);
  callback(entry.rows);

  return () => {
    const current = sharedViewSubscriptions.get(entry.scopeKey);
    if (!current) return;

    current.listeners.delete(callback);
    if (current.listeners.size > 0) return;

    current.unsubscribe?.();
    sharedViewSubscriptions.delete(entry.scopeKey);
  };
}

/**
 * Subscribes to views for a specific user.
 *
 * @param {Object} options - Options to scope views.
 * @param {string} [options.userKey] - The user key to scope views.
 * @param {string} [options.baseKey] - The base key to scope views.
 * @param {string} [options.nodeType] - The node type key to scope views.
 * @param {function} callback - Callback function to handle real-time updates.
 *
 * @returns {function(): void} Unsubscribe function
 */
export function subscribeToViews({ userKey, baseKey, nodeType } = {}, callback) {

  // Validate callback
  if (typeof callback !== 'function') {
    return () => { };
  }

  const scope = buildViewScope({ userKey, baseKey, nodeType });
  const constraints = buildViewConstraints(scope);

  // Disallow accidental full-collection subscriptions.
  if (!constraints.length) {
    callback([]);
    return () => { };
  }

  // Base-only (or user-only) subscriptions: no system-type merge.
  if (!scope.nodeType) {
    return attachSharedListener(scope, callback);
  }

  // Type-scoped: merge live Admin system views with base/user views.
  let baseRows = [];
  let systemRows = [];
  let cancelled = false;

  const emit = () => {
    if (cancelled) return;
    callback(mergeSystemAndBaseViews(systemRows, baseRows));
  };

  const unsubscribeBase = attachSharedListener(scope, (rows) => {
    baseRows = Array.isArray(rows) ? rows : [];
    emit();
  });

  const unsubscribeSystem = subscribeToTypeViews(scope.nodeType, (rows) => {
    systemRows = Array.isArray(rows) ? rows : [];
    emit();
  });

  return () => {
    cancelled = true;
    unsubscribeBase();
    unsubscribeSystem();
  };
}

export const subscribeToViewsShared = subscribeToViews;

/**
 * Subscribes to a single view by key.
 *
 * @param {string} viewKey - View document id.
 * @param {function} callback - Callback function to handle real-time updates.
 *
 * @returns {function(): void} Unsubscribe function
 */
export function subscribeToView(viewKey, callback) {

  // Validate callback
  if (typeof callback !== 'function') {
    return () => { };
  }

  // Validate viewKey
  if (!viewKey) {
    callback(null);
    return () => { };
  }

  return onSnapshot(
    doc(db, VIEWS_COLLECTION, viewKey),
    (docSnap) => {
      const data = docSnap.exists()
        ? { id: docSnap.id, ...(docSnap.data() || {}) }
        : null;
      callback(data);
    },
    (error) => {
      console.error('[ViewReadService] subscribeToView error:', error);
      callback(null);
    }
  );
}
